import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useEnqueueRich } from '../context/ToastContext';

const LS_KEY = 'loyalty_last_seen_xp';

function getLastSeen() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function setLastSeen(xp, level) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ xp, level })); } catch {}
}

export default function XpToast() {
  const { user } = useAuth();
  const enqueueRich = useEnqueueRich();
  const pendingRef = useRef([]);
  const enqueuedRef = useRef(false);

  useEffect(() => {
    if (!user || !user.level_data) return;

    const currentXp = user.xp || user.level_data.xp || 0;
    const currentLevel = user.level_data.level || 1;
    const last = getLastSeen();

    if (!last) {
      setLastSeen(currentXp, currentLevel);
      return;
    }

    const loginEvent = window.__pendingXpEvent;
    if (loginEvent) {
      delete window.__pendingXpEvent;
      const items = [];

      if (loginEvent.daily_xp > 0) {
        items.push({ xp: loginEvent.daily_xp, label: 'Daily Login Bonus', icon: '📅' });
      }
      if (loginEvent.streak_xp > 0) {
        items.push({ xp: loginEvent.streak_xp, label: loginEvent.rewards?.find(r => r.type === 'streak')?.detail || 'Login Streak Bonus', icon: '🔥' });
      }
      if (loginEvent.levels_gained?.length > 0) {
        for (const lvl of loginEvent.levels_gained) {
          const reward = loginEvent.rewards?.find(r => r.level === lvl);
          if (reward?.type === 'spins') {
            items.push({ xp: 0, label: `Level ${lvl}! +${reward.spin_value} Spins`, icon: '🎀', isLevelUp: true, level: lvl });
          } else if (reward?.type === 'coupon') {
            items.push({ xp: 0, label: `Level ${lvl}! Earned ${reward.coupon_detail}`, icon: '🎁', isLevelUp: true, level: lvl });
          } else if (reward?.type === 'points') {
            items.push({ xp: 0, label: `Level ${lvl}! +${reward.points_value} Bud Points`, icon: '⬆️', isLevelUp: true, level: lvl });
          } else {
            items.push({ xp: 0, label: `Level ${lvl}!`, icon: '⬆️', isLevelUp: true, level: lvl });
          }
        }
      }

      if (loginEvent.level_data?.title) {
        const levelUpItems = items.filter(i => i.isLevelUp);
        if (levelUpItems.length === 1) levelUpItems[0].subtitle = loginEvent.level_data.title;
      }

      let toEnqueue = [];
      if (items.length > 0 && items.length <= 2 && !items.some(i => i.isLevelUp)) {
        const totalXp = items.reduce((sum, i) => sum + i.xp, 0);
        toEnqueue.push({ type: 'xp', xp: totalXp, label: items.map(i => i.label).join(' + '), icon: '📈', levelData: user.level_data });
      } else {
        toEnqueue = items.map(item => ({ type: 'xp', ...item, levelData: user.level_data }));
      }

      setLastSeen(currentXp, currentLevel);
      toEnqueue.forEach(item => enqueueRich(item));
      return;
    }

    if (currentXp > last.xp) {
      const xpGained = currentXp - last.xp;
      const leveledUp = currentLevel > last.level;

      if (leveledUp) {
        enqueueRich({
          type: 'xp', xp: 0, label: `Level ${currentLevel}!`,
          subtitle: user.level_data.title, icon: '⬆️',
          isLevelUp: true, level: currentLevel, levelData: user.level_data,
        });
      }
      enqueueRich({
        type: 'xp', xp: xpGained, label: leveledUp ? 'Keep it up!' : 'XP Earned',
        icon: '📈', levelData: user.level_data,
      });
      setLastSeen(currentXp, currentLevel);
      return;
    }

    if (currentXp !== last.xp || currentLevel !== last.level) {
      setLastSeen(currentXp, currentLevel);
    }
  }, [user, enqueueRich]);

  // Clear stored XP on logout
  useEffect(() => {
    if (!user) {
      try { localStorage.removeItem(LS_KEY); } catch {}
    }
  }, [user]);

  return null;
}
